import type { PDFDocumentProxy } from 'pdfjs-dist'

// Cap canvas resolution to bound memory: a Letter page at DPR 2 is already ~20MB.
const PDFJS_DPR_CAP = 2

export const cappedPixelRatio = () =>
  Math.min(window.devicePixelRatio || 1, PDFJS_DPR_CAP)

// Files pdf.js fetches while rendering, served by the pdfjs-assets plugin in vite.config.js. Without
// the wasm decoders a scanned page (JBIG2 or JPEG 2000) renders blank; without the others, CJK text,
// non-embedded fonts, and colour-managed images fall back to guesses. Trailing slashes are required.
const PDFJS_DOCUMENT_OPTIONS = {
  wasmUrl: '/pdfjs/wasm/',
  cMapUrl: '/pdfjs/cmaps/',
  standardFontDataUrl: '/pdfjs/standard_fonts/',
  iccUrl: '/pdfjs/iccs/',
  // Range-fetch only the rendered pages, not the whole file; pdf.js falls back to a full fetch if the host can't serve byte ranges.
  disableAutoFetch: true,
  disableStream: false,
} as const

// Each open document costs a worker thread and the bytes it fetched, so only a few stay open once
// nobody is reading them. A document someone is still reading is never closed.
const IDLE_DOCUMENTS = 4

export interface HeldDocument {
  promise: Promise<PDFDocumentProxy>
  release: () => void
}

interface OpenDocument {
  promise: Promise<PDFDocumentProxy>
  holders: number
  close: () => void
}

// pdf.js is about 400KB, so it is fetched with the first document, not with the page.
const loadToolkit = () =>
  Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?worker'),
  ]).then(([library, worker]) => ({ library, PdfjsWorker: worker.default }))

let toolkit: ReturnType<typeof loadToolkit> | null = null

const pdfToolkit = () => {
  toolkit ??= loadToolkit()
  return toolkit
}

// A file still on the user's machine, opened once to read its page count and closed at once.
export const countPdfPages = async (file: File) => {
  const { library, PdfjsWorker } = await pdfToolkit()
  const worker = new library.PDFWorker({
    // @ts-expect-error pdf.js's generated types declare `port` as null; it accepts a Worker.
    port: new PdfjsWorker(),
  })
  const data = new Uint8Array(await file.arrayBuffer())
  const task = library.getDocument({ data, worker, ...PDFJS_DOCUMENT_OPTIONS })
  try {
    return (await task.promise).numPages
  } finally {
    await task.destroy()
    worker.destroy()
  }
}

// Insertion order is the recency order: a document is moved to the end each time it is asked for.
const documents = new Map<string, OpenDocument>()

const open = (url: string): OpenDocument => {
  let closed = false
  let teardown = () => {}
  const promise = pdfToolkit().then(({ library, PdfjsWorker }) => {
    // Fresh worker per document. A shared one gets torn down when any document is destroyed,
    // leaving the next getDocument with a dead worker.
    const worker = new library.PDFWorker({
      // @ts-expect-error pdf.js's generated types declare `port` as null; it accepts a Worker.
      port: new PdfjsWorker(),
    })
    const task = library.getDocument({ url, worker, ...PDFJS_DOCUMENT_OPTIONS })
    // Await the task teardown before terminating its worker, per pdf.js.
    teardown = () => task.destroy().finally(() => worker.destroy())
    if (closed) teardown()
    return task.promise
  })
  // A file that will not open is forgotten at once, so the next reader gets a fresh try.
  promise.catch(() => documents.delete(url))
  return {
    promise,
    holders: 0,
    close: () => {
      closed = true
      teardown()
    },
  }
}

const closeIdle = () => {
  const idle = [...documents].filter(([, held]) => held.holders === 0)
  while (idle.length > IDLE_DOCUMENTS) {
    const oldest = idle.shift()
    if (!oldest) return
    const [url, held] = oldest
    documents.delete(url)
    held.close()
  }
}

// One open document per file, shared by every reader of it: the strip's thumbnails and the page
// readers. Opening it per reader would fetch and parse the whole PDF again on every click.
export const acquireDocument = (url: string): HeldDocument => {
  let held = documents.get(url)
  if (held) {
    documents.delete(url)
  } else {
    held = open(url)
  }
  documents.set(url, held)
  held.holders += 1

  let released = false
  const opened = held
  return {
    promise: opened.promise,
    release: () => {
      if (released) return
      released = true
      opened.holders -= 1
      closeIdle()
    },
  }
}
